import { useRef, useState } from 'react';
import { Link, Route, Routes, useNavigate } from 'react-router-dom';
import { kanal } from '@beholder/shared';
import type { WbTafel } from '@beholder/shared/module/whiteboard';
import type { ModulSeiteProps, WebModul } from '../../core/modul';
import { api, modulPfad } from '../../core/api';
import { useLive } from '../../core/live';
import { Symbol } from '../../core/symbole';
import { Dialog, Eingabe, Feld, Karte as UiKarte, Knopf, Laden, Leer, Meldung, datumZeit, useAktion, useDaten } from '../../core/ui';
import { TafelSeite } from './Brett';
import './stil.css';

function vorschauAdresse(svg: string) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Tafel-Liste neu laden, sobald sich etwas ändert (höchstens einmal pro Sekunde). */
function useTafeln(projektId: string) {
  const d = useDaten(() => api.get<WbTafel[]>(modulPfad(projektId, 'whiteboard', 'tafeln')), [projektId]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useLive(kanal(projektId, 'whiteboard'), () => {
    if (timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void d.neuLaden();
    }, 1000);
  });
  return d;
}

function Vorschau({ t, klein }: { t: WbTafel; klein?: boolean }) {
  return (
    <div className={`wb-vorschau${klein ? ' wb-vorschau-klein' : ''}${t.farbe === 'dunkel' ? ' wb-vorschau-dunkel' : ''}`}>
      {t.vorschau && t.elementzahl > 0 ? <img src={vorschauAdresse(t.vorschau)} alt="" loading="lazy" /> : <span className="leise klein">leer</span>}
    </div>
  );
}

function TafelListe(p: ModulSeiteProps) {
  const { daten, fehler, laedt, neuLaden } = useTafeln(p.projekt.id);
  const nav = useNavigate();
  const [neu, setNeu] = useState(false);
  const [umbenennen, setUmbenennen] = useState<WbTafel | null>(null);
  const [loeschen, setLoeschen] = useState<WbTafel | null>(null);
  const [name, setName] = useState('');
  const aktion = useAktion();

  const anlegen = async () => {
    const t = await aktion.ausfuehren(() => api.post<WbTafel>(modulPfad(p.projekt.id, 'whiteboard', 'tafeln'), { name }));
    if (t) {
      setNeu(false);
      nav(t.id);
    }
  };
  const speichernName = async () => {
    if (!umbenennen) return;
    const ok = await aktion.ausfuehren(() => api.patch(modulPfad(p.projekt.id, 'whiteboard', `tafeln/${umbenennen.id}`), { name }));
    if (ok) {
      setUmbenennen(null);
      void neuLaden();
    }
  };
  const entfernen = async () => {
    if (!loeschen) return;
    const ok = await aktion.ausfuehren(() => api.delete(modulPfad(p.projekt.id, 'whiteboard', `tafeln/${loeschen.id}`)));
    if (ok) {
      setLoeschen(null);
      void neuLaden();
    }
  };

  return (
    <div className="seite">
      <div className="seiten-kopf">
        <h2>Tafeln</h2>
        {p.darfSchreiben && (
          <Knopf
            art="haupt"
            onClick={() => {
              setName(`Tafel ${(daten?.length ?? 0) + 1}`);
              aktion.setFehler('');
              setNeu(true);
            }}
          >
            {Symbol.plus} Neue Tafel
          </Knopf>
        )}
      </div>
      <Meldung>{fehler}</Meldung>
      {laedt && !daten && <Laden />}
      {daten?.length === 0 && (
        <Leer titel="Noch keine Tafeln">{p.darfSchreiben ? 'Leg die erste Tafel an, um gemeinsam zu zeichnen.' : 'Hier gibt es noch nichts zu sehen.'}</Leer>
      )}
      <div className="wb-raster">
        {daten?.map((t) => (
          <article key={t.id} className="wb-kachel">
            <Link to={t.id} className="wb-kachel-link" aria-label={`Tafel ${t.name} öffnen`}>
              <Vorschau t={t} />
              <div className="wb-kachel-text">
                <strong>{t.name}</strong>
                <span className="leise klein">
                  {t.elementzahl === 1 ? '1 Element' : `${t.elementzahl} Elemente`} · geändert {datumZeit(t.geaendertAm)}
                </span>
              </div>
            </Link>
            {p.darfSchreiben && (
              <div className="wb-kachel-aktionen">
                <Knopf
                  art="leise"
                  klein
                  onClick={() => {
                    setName(t.name);
                    aktion.setFehler('');
                    setUmbenennen(t);
                  }}
                >
                  Umbenennen
                </Knopf>
                <Knopf
                  art="leise"
                  klein
                  aria-label={`Tafel ${t.name} löschen`}
                  onClick={() => {
                    aktion.setFehler('');
                    setLoeschen(t);
                  }}
                >
                  {Symbol.muell}
                </Knopf>
              </div>
            )}
          </article>
        ))}
      </div>

      <Dialog
        titel="Neue Tafel"
        offen={neu}
        schliessen={() => setNeu(false)}
        fuss={
          <>
            <Knopf art="haupt" onClick={anlegen} disabled={aktion.laeuft || !name.trim()}>
              Anlegen
            </Knopf>
            <Knopf onClick={() => setNeu(false)}>Abbrechen</Knopf>
          </>
        }
      >
        <form
          className="stapel"
          onSubmit={(e) => {
            e.preventDefault();
            void anlegen();
          }}
        >
          <Feld titel="Name">
            <Eingabe autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          </Feld>
          <Meldung>{aktion.fehler}</Meldung>
        </form>
      </Dialog>

      <Dialog
        titel="Tafel umbenennen"
        offen={!!umbenennen}
        schliessen={() => setUmbenennen(null)}
        fuss={
          <>
            <Knopf art="haupt" onClick={speichernName} disabled={aktion.laeuft || !name.trim()}>
              Speichern
            </Knopf>
            <Knopf onClick={() => setUmbenennen(null)}>Abbrechen</Knopf>
          </>
        }
      >
        <form
          className="stapel"
          onSubmit={(e) => {
            e.preventDefault();
            void speichernName();
          }}
        >
          <Feld titel="Name">
            <Eingabe autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          </Feld>
          <Meldung>{aktion.fehler}</Meldung>
        </form>
      </Dialog>

      <Dialog
        titel="Tafel in den Papierkorb legen?"
        offen={!!loeschen}
        schliessen={() => setLoeschen(null)}
        fuss={
          <>
            <Knopf art="gefahr" onClick={entfernen} disabled={aktion.laeuft}>
              In den Papierkorb
            </Knopf>
            <Knopf onClick={() => setLoeschen(null)}>Abbrechen</Knopf>
          </>
        }
      >
        <p>
          Die Tafel „{loeschen?.name}“ kommt mit allen Zeichnungen und Bildern in den{' '}
          <Link to={`/p/${p.projekt.id}/papierkorb`}>Papierkorb des Projekts</Link>. Dort lässt sie sich wiederherstellen.
        </p>
        <Meldung>{aktion.fehler}</Meldung>
      </Dialog>
    </div>
  );
}

function Seite(p: ModulSeiteProps) {
  return (
    <Routes>
      <Route index element={<TafelListe {...p} />} />
      <Route path=":tafelId" element={<TafelSeite {...p} />} />
    </Routes>
  );
}

function Karte(p: ModulSeiteProps) {
  const { daten } = useTafeln(p.projekt.id);
  const basis = `/p/${p.projekt.id}/whiteboard`;
  return (
    <UiKarte titel={<Link to={basis} className="wb-karte-titel">{Symbol.whiteboard} Whiteboard</Link>}>
      {!daten ? (
        <p className="leise">Lädt …</p>
      ) : daten.length === 0 ? (
        <p className="leise">Noch keine Tafeln.</p>
      ) : (
        <div className="stapel wb-karte">
          <span className="leise klein">{daten.length === 1 ? 'Eine Tafel' : `${daten.length} Tafeln`}</span>
          {daten.slice(0, 3).map((t) => (
            <Link key={t.id} to={`${basis}/${t.id}`} className="wb-karte-zeile">
              <Vorschau t={t} klein />
              <span>
                <strong>{t.name}</strong>
                <br />
                <span className="leise klein">geändert {datumZeit(t.geaendertAm)}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </UiKarte>
  );
}

const whiteboard: WebModul = { id: 'whiteboard', name: 'Whiteboard', reihenfolge: 30, symbol: Symbol.whiteboard, Seite, Karte };
export default whiteboard;
